import {Behavior} from './ToggleArea';
import FocusJail from './FocusJail';
import mergeOpts from './merge-opts';
import {delegate} from './events';
import {fd, mutate} from './promise-fastdom';
import focusElement from './focus-element';
import promiseRAF from './promise-raf';

const _ = new WeakMap();

export function ButtonBehavior(button, o) {

    o = mergeOpts({
        buttons: [],
        closeOnBlur: false,
        closeOnEsc: true,
        closeOnLinkClick: true,
        closeOnOutsideClick: true,
        focusAreaOnOpen: false,
        jail: true,
        openedOnInit: false,
    }, o);

    o.button = button;

    o.buttons.push(button);

    _.set(this, o);
}

ButtonBehavior.prototype = new Behavior();

ButtonBehavior.prototype.toggle = function () {
    const o = _.get(this);
    o.update(!this.active);
};

ButtonBehavior.prototype.init = function (instance) {

    const o = _.get(this);

    this.active = o.openedOnInit;

    if (o.jail === true) {
        o.jail = new FocusJail([o.button, instance.area()]);
    }

    this.active && o.jail && o.jail.jail();

    o.update = (active) => {
        this.active = active;
        o.button.setAttribute('aria-expanded', this.active);
        instance.update();

        return this.active
            ? Promise.resolve()
                .then(promiseRAF())
                .then(mutate(() => {
                    o.jail && o.jail.jail();
                    if (o.focusAreaOnOpen && document.activeElement === o.button) {
                        focusElement.call(instance.area(), true);
                    }
                }))
            : Promise.resolve()
                .then(mutate(() => {
                    o.jail && o.jail.free();
                    if (instance.area().contains(document.activeElement)) {
                        focusElement.call(o.button, true);
                    }
                }))
            ;
    };

    o.toggleHandler = e => {
        e.preventDefault();
        o.update(!this.active);
    };

    o.keydownToggleHandler = e => {
        if (e.code === 'Space' || e.code === 'Enter') {
            e.preventDefault();
            o.toggleHandler(e);
        }
    };

    if (o.closeOnLinkClick) {
        o.closeOnLinkClickHandler = delegate('a[href]', () => o.update(false));
        instance.area().addEventListener('click', o.closeOnLinkClickHandler);
    }

    if (o.closeOnOutsideClick) {
        o.outsideClickHandler = (e) => {
            if (!this.active) {
                return;
            }
            var node = e.target;
            while (node) {
                if (node === o.button || node === instance.area()) {
                    // a parent of the target is either the toggle button or the area
                    return;
                }
                node = node.parentNode;
            }

            o.update(false);
        };
        document.addEventListener('click', o.outsideClickHandler, {passive: true});
    }

    if (o.closeOnEsc) {
        o.escapeHandler = (e) => {
            // press on ESC
            if (!this.active) {
                return;
            }
            if (e.code !== 'Escape') {
                return;
            }
            if (o.jail && !o.jail.contains(document.activeElement)) {
                return;
            }
            o.update(false);
        };
        document.addEventListener('keydown', o.escapeHandler, {passive: true});
    }

    if (o.closeOnBlur) {
        o.focusHandler = (e) => {
            if (e.type === 'focusout') {
                o.focusHandlerAF = requestAnimationFrame(() => {
                    o.update(false);
                });
            } else {
                cancelAnimationFrame(o.focusHandlerAF);
            }
        };
        instance.area().addEventListener('focusout', o.focusHandler);
        instance.area().addEventListener('focusin', o.focusHandler);
    }

    // o.button.addEventListener('click', o.toggleHandler);

    if (instance.area().id) {
        o.delegateToggleHandler = delegate(`a[href="#${instance.area().id}"]`, (e) => {
            focusElement.call(o.button, true);
            o.toggleHandler(e);
        });
        document.addEventListener('click', o.delegateToggleHandler);
    }

    return fd.mutate(() => {

        o.buttons.forEach(button => {
            button.addEventListener('click', o.toggleHandler);

            if (button.tagName === 'A') {
                button.setAttribute('data-href', o.button.href);
                // button.setAttribute('href', '#');
                button.removeAttribute('href');
            }

            if (button.tagName !== 'BUTTON') {
                button.setAttribute('role', 'button');
                button.tabIndex = 0;
                button.addEventListener('keydown', o.keydownToggleHandler);
            }
        });

        o.button.disabled = false;
        o.button.setAttribute('aria-controls', instance.area().id);
        o.button.setAttribute('aria-haspopup', 'true');
        o.button.setAttribute('aria-expanded', this.active);
    });
};

ButtonBehavior.prototype.jail = function () {
    const o = _.get(this);
    return o.jail;
};

ButtonBehavior.prototype.toggleClass = function (className, toggle) {
    const o = _.get(this);
    o.button.classList.toggle(className, toggle);
};

ButtonBehavior.prototype.destroy = function (instance) {
    const o = _.get(this);
    o.button.removeEventListener('click', o.toggleHandler);

    if (o.closeOnLinkClick) {
        instance.area().removeEventListener('click', o.closeOnLinkClickHandler);
    }

    if (o.closeOnOutsideClick) {
        document.removeEventListener('click', o.outsideClickHandler);
    }

    if (o.closeOnEsc) {
        document.removeEventListener('keydown', o.escapeHandler);
    }

    if (o.closeOnBlur) {
        instance.area().removeEventListener('focusout', o.focusHandler);
        instance.area().removeEventListener('focusin', o.focusHandler);
    }

    if (instance.area().id) {
        document.removeEventListener('click', o.delegateToggleHandler);
    }

    o.jail && o.jail.destroy();

    return fd.mutate(() => {

        o.buttons.forEach(button => {
            button.removeEventListener('click', o.toggleHandler);

            if (o.button.tagName === 'A') {
                o.button.setAttribute('href', o.button.getAttribute('data-href'));
                o.button.removeAttribute('data-href');
            }

            if (button.tagName !== 'BUTTON') {
                button.removeAttribute('role');
                button.tabIndex = null;
                button.removeEventListener('keydown', o.keydownToggleHandler);
            }
        });

        o.button.removeAttribute('aria-controls');
        o.button.removeAttribute('aria-haspopup');
        o.button.removeAttribute('aria-expanded');
        _.delete(this);
    });
};
